"use client";

import * as React from "react";
import { SideOverlayDrawer } from "./side-overlay-drawer";
import { SocialConnectButton, SocialConnectButtonProps } from "./social-connect-button";
import { Button } from "./button";
import { Input } from "./input";

const enum EditMode {
  NONE = "none",
  PERSONAL_INFO = "personal_info",
}

export interface AccountDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  personalInfo: {
    name: string;
    email: string;
    phone: string;
  };
  onEditPersonalInfo?: () => void;
  onUpdatePersonalInfo?: (data: { name: string; email: string }) => Promise<void>;
  onCheckEmailAvailability?: (email: string) => Promise<{ available: boolean }>;
  onChangePassword?: () => void;
  socialNetworks: SocialConnectButtonProps[];
  onDeleteAccount?: () => void;
}

export function AccountDrawer({
  isOpen,
  onClose,
  personalInfo,
  onEditPersonalInfo,
  onUpdatePersonalInfo,
  onCheckEmailAvailability,
  onChangePassword,
  socialNetworks,
  onDeleteAccount,
}: AccountDrawerProps) {
  const [editMode, setEditMode] = React.useState<EditMode>(EditMode.NONE);
  const [formData, setFormData] = React.useState({
    name: personalInfo.name,
    email: personalInfo.email,
  });
  const [errors, setErrors] = React.useState<{
    name?: string;
    email?: string;
    submit?: string;
  }>({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = React.useState(false);
  const [emailCheckTimeout, setEmailCheckTimeout] = React.useState<NodeJS.Timeout | null>(null);

  // Reset form when drawer opens/closes
  React.useEffect(() => {
    if (isOpen) {
      setFormData({
        name: personalInfo.name,
        email: personalInfo.email,
      });
      setErrors({});
      setIsCheckingEmail(false);
    } else {
      // Reset edit mode when drawer closes
      setEditMode(EditMode.NONE);
    }
  }, [isOpen]);

  // Update form data when personal info changes (but don't reset edit mode)
  React.useEffect(() => {
    if (isOpen && editMode === EditMode.NONE) {
      setFormData({
        name: personalInfo.name,
        email: personalInfo.email,
      });
    }
  }, [personalInfo.name, personalInfo.email, isOpen, editMode]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (emailCheckTimeout) {
        clearTimeout(emailCheckTimeout);
      }
    };
  }, [emailCheckTimeout]);

  const checkEmailAvailability = React.useCallback(async (email: string) => {
    if (!onCheckEmailAvailability || email.toLowerCase() === personalInfo.email.toLowerCase()) {
      return;
    }

    setIsCheckingEmail(true);
    try {
      const result = await onCheckEmailAvailability(email);
      if (!result.available) {
        setErrors(prev => ({ ...prev, email: "Email is already in use" }));
      }
    } catch (error) {
      setErrors(prev => ({ ...prev, email: "Failed to check email availability" }));
    } finally {
      setIsCheckingEmail(false);
    }
  }, [onCheckEmailAvailability, personalInfo.email]);

  const validateForm = async () => {
    const newErrors: typeof errors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.trim().length > 100) {
      newErrors.name = "Name must be at most 100 characters";
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        newErrors.email = "Please enter a valid email address";
      } else if (formData.email.trim().length > 255) {
        newErrors.email = "Email must be at most 255 characters";
      }
    }

    // If basic validation passes and email changed, check availability
    if (Object.keys(newErrors).length === 0 && formData.email.toLowerCase() !== personalInfo.email.toLowerCase()) {
      if (onCheckEmailAvailability) {
        setIsCheckingEmail(true);
        try {
          const result = await onCheckEmailAvailability(formData.email.trim());
          if (!result.available) {
            newErrors.email = "Email is already in use";
          }
        } catch (error) {
          newErrors.email = "Failed to check email availability";
        } finally {
          setIsCheckingEmail(false);
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEditPersonalInfo = () => {
    setEditMode(EditMode.PERSONAL_INFO);
    // Only call the callback if it exists, but don't depend on it for edit mode
    onEditPersonalInfo?.();
  };

  const handleCancelEdit = () => {
    setEditMode(EditMode.NONE);
    setFormData({
      name: personalInfo.name,
      email: personalInfo.email,
    });
    setErrors({});
  };

  const handleSavePersonalInfo = async () => {
    const isValid = await validateForm();
    if (!isValid) {
      return;
    }

    if (!onUpdatePersonalInfo) {
      console.error("onUpdatePersonalInfo is not provided");
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      await onUpdatePersonalInfo({
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
      });
      
      setEditMode(EditMode.NONE);
    } catch (error) {
      setErrors({
        submit: error instanceof Error ? error.message : "Failed to update personal information",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }

    // Debounced email availability check
    if (field === "email" && onCheckEmailAvailability) {
      if (emailCheckTimeout) {
        clearTimeout(emailCheckTimeout);
      }
      
      const timeout = setTimeout(() => {
        const trimmedEmail = value.trim();
        if (trimmedEmail && trimmedEmail.toLowerCase() !== personalInfo.email.toLowerCase()) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (emailRegex.test(trimmedEmail)) {
            checkEmailAvailability(trimmedEmail);
          }
        }
      }, 500);
      
      setEmailCheckTimeout(timeout);
    }
  };

  return (
    <SideOverlayDrawer isOpen={isOpen} onClose={onClose} title="Account" onBack={onClose}>
      <div className="space-y-4">
        <div className="rounded-xl border border-black bg-white p-3">
          <div className="flex items-center justify-between text-sm font-medium text-[#101828]">
            <span>Personal Information</span>
            {editMode === EditMode.PERSONAL_INFO ? (
              <div className="flex gap-2">
                <button 
                  type="button" 
                  className="text-[#667085]" 
                  onClick={handleCancelEdit}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="text-[#0D41E1]" 
                  onClick={handleSavePersonalInfo}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Save"}
                </button>
              </div>
            ) : (
              <button type="button" className="text-[#667085]" onClick={handleEditPersonalInfo}>
                Edit
              </button>
            )}
          </div>
          
          {errors.submit && (
            <div className="mt-2 text-sm text-red-600">
              {errors.submit}
            </div>
          )}

          <div className="mt-4 space-y-3 text-sm text-[#475467]">
            <div>
              <p className="text-xs text-[#667085]">Name</p>
              {editMode === EditMode.PERSONAL_INFO ? (
                <div className="mt-1">
                  <Input
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    placeholder="Enter your name"
                    className="h-[42px]"
                    disabled={isSubmitting}
                  />
                  {errors.name && (
                    <p className="mt-1 text-xs text-red-600">{errors.name}</p>
                  )}
                </div>
              ) : (
                <div className="mt-1 rounded-lg border border-black px-3 py-2 bg-white">
                  {personalInfo.name}
                </div>
              )}
            </div>
            
            <div>
              <p className="text-xs text-[#667085]">Email ID</p>
              {editMode === EditMode.PERSONAL_INFO ? (
                <div className="mt-1">
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    placeholder="Enter your email"
                    className="h-[42px]"
                    disabled={isSubmitting}
                  />
                  {isCheckingEmail && (
                    <p className="mt-1 text-xs text-[#667085]">Checking availability...</p>
                  )}
                  {errors.email && (
                    <p className="mt-1 text-xs text-red-600">{errors.email}</p>
                  )}
                </div>
              ) : (
                <div className="mt-1 rounded-lg border border-black px-3 py-2 bg-white">
                  {personalInfo.email}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-black bg-white p-3">
          <div className="text-sm font-medium text-[#101828]">Social media connected</div>
          <div className="mt-3 space-y-2">
            {socialNetworks.map((network) => (
              <SocialConnectButton key={network.label} {...network} />
            ))}
          </div>
        </div>
      </div>
    </SideOverlayDrawer>
  );
}
